import { themePresets, ThemePreset } from '../config/themePresets';
import { useTheme } from '../contexts/ThemeContext';

interface ThemeSelectorProps {
    onSelectPreset?: (preset: ThemePreset) => void;
    showPreview?: boolean;
}

export default function ThemeSelector({ onSelectPreset, showPreview = true }: ThemeSelectorProps) {
    const { applyTheme, primaryColor, backgroundColor } = useTheme();

    const handleSelect = (preset: ThemePreset) => {
        applyTheme(preset.colors);
        onSelectPreset?.(preset);
    };

    // Find current active preset
    const activePreset = themePresets.find(
        p => p.colors.primaryColor === primaryColor && p.colors.backgroundColor === backgroundColor
    );

    return (
        <div>
            <h3 className="text-lg font-medium mb-4 text-text-primary">Theme Presets</h3>
            <p className="text-sm text-text-secondary mb-4">
                Choose a preset theme or customize colors individually below
            </p>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {themePresets.map((preset) => {
                    const isActive = activePreset?.id === preset.id;
                    return (
                        <button
                            key={preset.id}
                            onClick={() => handleSelect(preset)}
                            className={`p-4 rounded-lg border-2 transition-all text-left ${isActive
                                    ? 'border-primary ring-2 ring-primary/20'
                                    : 'border-border hover:border-primary/50'
                                }`}
                        >
                            {showPreview && (
                                <div
                                    className="w-full h-16 rounded-md mb-3 flex items-center justify-center text-2xl"
                                    style={{
                                        backgroundColor: preset.colors.backgroundColor,
                                        border: `2px solid ${preset.colors.primaryColor}`
                                    }}
                                >
                                    <span
                                        className="text-xl"
                                        style={{ color: preset.colors.primaryColor }}
                                    >
                                        {preset.preview}
                                    </span>
                                </div>
                            )}
                            <div className="flex items-center gap-2 mb-1">
                                <span>{preset.preview}</span>
                                <span className="font-medium text-text-primary">{preset.name}</span>
                            </div>
                            <p className="text-xs text-text-secondary">{preset.description}</p>

                            {/* Color swatches */}
                            <div className="flex gap-1 mt-2">
                                <div
                                    className="w-5 h-5 rounded-full border border-white/20"
                                    style={{ backgroundColor: preset.colors.primaryColor }}
                                    title="Primary"
                                />
                                <div
                                    className="w-5 h-5 rounded-full border border-white/20"
                                    style={{ backgroundColor: preset.colors.secondaryColor }}
                                    title="Secondary"
                                />
                                <div
                                    className="w-5 h-5 rounded-full border border-gray-300"
                                    style={{ backgroundColor: preset.colors.backgroundColor }}
                                    title="Background"
                                />
                            </div>
                        </button>
                    );
                })}
            </div>
        </div>
    );
}
